import type { GQL_ID } from '@@partner:types:graphql'
import type { Category } from '@@partner:types:app'

import React, { useCallback, useState, useEffect, useRef } from 'react'
import { Tree, Panel, Loader, Message, IconButton } from 'rsuite'
import { generatePath, useHistory } from 'react-router-dom'
import EditIcon from '@rsuite/icons/Edit'

import { useCategoriesFetcher } from '@@partner:graphql/query/catalog/categoriesWithParent.gql'
import { ROUTE_CATALOG_CATEGORY_EDIT } from '@@partner:config/routes'

import './CategoryTree.scss'

interface TreeNode {
  value: string
  label: string
  children?: TreeNode[]
}

export interface CategoryTreeProps {
  brandSlug: string
}

function mapCategoryToTreeNode (category: Category): TreeNode {
  return {
    value: String(category.id),
    label: `${category.name} (ID: ${category.id})`,
    children: []
  }
}

function updateNodeChildren (
  nodes: TreeNode[],
  targetValue: string,
  children: TreeNode[]
): TreeNode[] {
  return nodes.map(node => {
    if (node.value === targetValue) {
      return { ...node, children }
    }
    if (node.children && node.children.length > 0) {
      return { ...node, children: updateNodeChildren(node.children, targetValue, children) }
    }
    return node
  })
}

export default function CategoryTree ({ brandSlug }: CategoryTreeProps) {
  const [ treeNodes, setTreeNodes ] = useState<TreeNode[]>([])
  const [ rootLoading, setRootLoading ] = useState(false)
  const [ rootError, setRootError ] = useState<Error | null>(null)
  const [ expandedKeys, setExpandedKeys ] = useState<string[]>([])
  const loadedKeysRef = useRef<Set<string>>(new Set())
  const history = useHistory()

  const { fetchRootCategories, fetchCategories } = useCategoriesFetcher()

  // Загружаем корневые категории при монтировании
  useEffect(() => {
    const loadRoot = async () => {
      setRootLoading(true)
      setRootError(null)
      try {
        const data = await fetchRootCategories({ skipIds: [] })
        setTreeNodes(data.categories.edges.map(mapCategoryToTreeNode))
      }
      catch (err) {
        setRootError(err as Error)
      }
      finally {
        setRootLoading(false)
      }
    }

    loadRoot()
  }, [ fetchRootCategories ])

  // Подгрузка дочерних категорий при раскрытии узла
  const handleExpand = useCallback(async (
    expandItemValues: any[],
    activeNode: any
  ) => {
    const nodeValue = String(activeNode.value)
    setExpandedKeys(expandItemValues.map(String))

    // Если уже загружали детей этого узла — пропускаем
    if (loadedKeysRef.current.has(nodeValue)) return

    // Если сворачивают — не грузим
    if (!expandItemValues.map(String).includes(nodeValue)) return

    loadedKeysRef.current.add(nodeValue)

    try {
      const data = await fetchCategories({ parentId: nodeValue, skipIds: [] })
      const children = data.categories.edges.map(mapCategoryToTreeNode)

      setTreeNodes(prev => updateNodeChildren(prev, nodeValue, children))
    }
    catch (err) {
      console.error('Ошибка загрузки субкатегорий:', err)
      loadedKeysRef.current.delete(nodeValue)
    }
  }, [ fetchCategories ])

  const generateEditUrl = useCallback(
    (categoryId: GQL_ID) => {
      if (!brandSlug) return ''
      return generatePath(ROUTE_CATALOG_CATEGORY_EDIT, {
        brandSlug,
        categoryId
      })
    },
    [ brandSlug ]
  )

  const renderTreeNode = useCallback(
    (node: any) => {
      const categoryId = node.value
      const editUrl = generateEditUrl(categoryId)

      return (
        <span className="CategoryTree__node">
          <span className="CategoryTree__label">{node.label}</span>
          <IconButton
            className="CategoryTree__editBtn"
            icon={<EditIcon />}
            size="xs"
            appearance="subtle"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              history.push(editUrl)
            }}
          />
        </span>
      )
    },
    [ generateEditUrl, history ]
  )

  return (
    <Panel
      className="CategoryTree"
      header="Дерево категорий"
      bordered
    >
      {rootLoading && (
        <Loader center content="Загрузка дерева категорий..." />
      )}

      {rootError && (
        <Message type="error">
          Ошибка загрузки: {rootError.message}
        </Message>
      )}

      {!rootLoading && !rootError && treeNodes.length === 0 && (
        <Message type="info">
          Категории не найдены
        </Message>
      )}

      {!rootLoading && !rootError && treeNodes.length > 0 && (
        <Tree
          data={treeNodes}
          expandItemValues={expandedKeys}
          onExpand={handleExpand}
          renderTreeNode={renderTreeNode}
          className="CategoryTree__tree"
        />
      )}
    </Panel>
  )
}
